import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { templatesApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, GripVertical, Save, Pencil, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useConfirm } from '../components/ConfirmProvider';

const FIELD_TYPES = [
  { value: 'textarea', label: 'Text Area (multi-line)' },
  { value: 'text', label: 'Text (single line)' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'signature', label: 'Signature' },
  { value: 'duration_hm', label: 'Duration (Hours & Minutes)' },
  { value: 'photo_upload', label: 'Photo Upload' },
  { value: 'medication_admin', label: 'Medication Administration (10 slots)' },
];

export default function TemplateEditorPage() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingFields, setEditingFields] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedField, setExpandedField] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await templatesApi.getAll();
      setTemplates(res.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setEditingFields(template.fields.map((f, i) => ({ ...f, _key: `field_${i}_${Date.now()}` })));
    setExpandedField(null);
  };

  const createBlankTemplate = () => {
    const blank = {
      id: null,
      name: '',
      type: 'custom',
      fields: [],
    };
    setSelectedTemplate(blank);
    setTemplateName('');
    setEditingFields([]);
    setExpandedField(null);
  };

  const addField = () => {
    const newField = {
      _key: `field_new_${Date.now()}`,
      name: `field_${editingFields.length + 1}`,
      label: '',
      type: 'textarea',
      required: false,
      placeholder: '',
      options: [],
    };
    setEditingFields([...editingFields, newField]);
    setExpandedField(newField._key);
  };

  const updateField = (key, updates) => {
    setEditingFields(prev => prev.map(f => f._key === key ? { ...f, ...updates } : f));
  };

  const removeField = async (key) => {
    if (!(await confirm({ title: 'Remove this field?', confirmText: 'Remove', destructive: true }))) return;
    setEditingFields(prev => prev.filter(f => f._key !== key));
  };

  const moveField = (index, direction) => {
    const newFields = [...editingFields];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setEditingFields(newFields);
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    const hasEmptyLabel = editingFields.some(f => !f.label.trim());
    if (hasEmptyLabel) {
      toast.error('All fields must have a label');
      return;
    }

    setSaving(true);
    try {
      const cleanFields = editingFields.map(f => {
        const field = {
          name: f.name || f.label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_'),
          label: f.label,
          type: f.type,
          required: f.required || false,
        };
        if (f.placeholder) field.placeholder = f.placeholder;
        if (f.options?.length > 0) field.options = f.options;
        if (f.med_slots) field.med_slots = f.med_slots;
        return field;
      });

      if (selectedTemplate.id) {
        // Update existing
        await templatesApi.update(selectedTemplate.id, {
          name: templateName,
          type: selectedTemplate.type,
          fields: cleanFields,
        });
        toast.success('Template saved successfully');
      } else {
        // Create new
        const res = await templatesApi.create({
          name: templateName,
          type: templateName.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_'),
          fields: cleanFields,
        });
        setSelectedTemplate(res.data);
        toast.success('New template created');
      }
      loadTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="template-editor-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="font-heading font-semibold text-slate-900">Template Editor</h1>
            </div>
            {selectedTemplate && (
              <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-template-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Template List - Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base">Templates</CardTitle>
                <CardDescription>Select a template to edit</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => selectTemplate(t)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedTemplate?.id === t.id
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`template-item-${t.id}`}
                  >
                    <p className="font-medium text-sm text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.fields?.length || 0} fields</p>
                  </button>
                ))}
                {templates.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No templates yet</p>
                )}
                <Button
                  variant="outline"
                  className="w-full mt-3 border-dashed border-slate-300 text-slate-600 hover:border-teal-500 hover:text-teal-600"
                  onClick={createBlankTemplate}
                  data-testid="create-blank-template-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Template
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Field Editor */}
          <div className="lg:col-span-3">
            {!selectedTemplate ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Pencil className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">Select a template from the left to start editing</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Template Name */}
                <Card>
                  <CardContent className="p-4">
                    <Label className="text-sm font-medium mb-2 block">Template Name</Label>
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      className="h-12 text-lg font-heading"
                      data-testid="template-name-input"
                    />
                  </CardContent>
                </Card>

                {/* Fields */}
                <div className="space-y-3">
                  {editingFields.map((field, index) => (
                    <Card key={field._key} className="border-slate-200" data-testid={`edit-field-${index}`}>
                      <CardContent className="p-0">
                        {/* Field Header - Always Visible */}
                        <div
                          className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => setExpandedField(expandedField === field._key ? null : field._key)}
                        >
                          <div className="flex flex-col gap-0.5">
                            <button
                              onClick={(e) => { e.stopPropagation(); moveField(index, -1); }}
                              disabled={index === 0}
                              className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); moveField(index, 1); }}
                              disabled={index === editingFields.length - 1}
                              className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900 truncate">
                                {field.label || 'Untitled Field'}
                              </span>
                              {field.required && <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5">Required</Badge>}
                            </div>
                            <span className="text-xs text-slate-400">{FIELD_TYPES.find(t => t.value === field.type)?.label || field.type}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-500 flex-shrink-0"
                            onClick={(e) => { e.stopPropagation(); removeField(field._key); }}
                            data-testid={`remove-field-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Expanded Editor */}
                        {expandedField === field._key && (
                          <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-4 bg-slate-50/50">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <Label className="text-xs text-slate-500">Field Label</Label>
                                <Input
                                  value={field.label}
                                  onChange={(e) => updateField(field._key, { label: e.target.value, name: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_') })}
                                  placeholder="e.g., Shift Summary"
                                  className="h-10"
                                  data-testid={`field-label-${index}`}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-slate-500">Field Type</Label>
                                <Select value={field.type} onValueChange={(v) => updateField(field._key, { type: v })}>
                                  <SelectTrigger className="h-10" data-testid={`field-type-${index}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {FIELD_TYPES.map((ft) => (
                                      <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs text-slate-500">Description / Helper Text</Label>
                              <Textarea
                                value={field.placeholder || ''}
                                onChange={(e) => updateField(field._key, { placeholder: e.target.value })}
                                placeholder="Shown above the field as guidance for the worker"
                                rows={2}
                                className="resize-none text-sm"
                                data-testid={`field-placeholder-${index}`}
                              />
                            </div>

                            {(field.type === 'select' || field.type === 'multiselect') && (
                              <div className="space-y-1.5">
                                <Label className="text-xs text-slate-500">Options (one per line)</Label>
                                <Textarea
                                  value={(field.options || []).join('\n')}
                                  onChange={(e) => updateField(field._key, { options: e.target.value.split('\n').filter(o => o.trim()) })}
                                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                                  rows={4}
                                  className="resize-none text-sm font-mono"
                                  data-testid={`field-options-${index}`}
                                />
                              </div>
                            )}

                            {field.type === 'medication_admin' && (
                              <div className="space-y-3">
                                <Label className="text-xs text-slate-500 font-medium">Configure 10 Medication Slots</Label>
                                <p className="text-xs text-slate-400">Name each medication slot and list the dropdown options workers can choose from.</p>
                                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                                  {Array.from({ length: 10 }, (_, slotIdx) => {
                                    const slots = field.med_slots || [];
                                    const slot = slots[slotIdx] || { name: '', options: [] };
                                    return (
                                      <div key={slotIdx} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`med-slot-${slotIdx}`}>
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded">{slotIdx + 1}</span>
                                          <Input
                                            value={slot.name}
                                            onChange={(e) => {
                                              const newSlots = [...(field.med_slots || Array.from({ length: 10 }, () => ({ name: '', options: [] })))];
                                              newSlots[slotIdx] = { ...newSlots[slotIdx], name: e.target.value };
                                              updateField(field._key, { med_slots: newSlots });
                                            }}
                                            placeholder={`e.g., Morning Medication, PRN Medication...`}
                                            className="h-8 text-sm flex-1"
                                            data-testid={`med-slot-name-${slotIdx}`}
                                          />
                                        </div>
                                        <Textarea
                                          value={(slot.options || []).join('\n')}
                                          onChange={(e) => {
                                            const newSlots = [...(field.med_slots || Array.from({ length: 10 }, () => ({ name: '', options: [] })))];
                                            newSlots[slotIdx] = { ...newSlots[slotIdx], options: e.target.value.split('\n').filter(o => o.trim()) };
                                            updateField(field._key, { med_slots: newSlots });
                                          }}
                                          placeholder="Paracetamol 500mg&#10;Ibuprofen 200mg&#10;Not administered"
                                          rows={2}
                                          className="resize-none text-xs font-mono"
                                          data-testid={`med-slot-options-${slotIdx}`}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-2">
                              <Switch
                                checked={field.required || false}
                                onCheckedChange={(v) => updateField(field._key, { required: v })}
                                data-testid={`field-required-${index}`}
                              />
                              <Label className="text-sm text-slate-600">Required field</Label>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Add Field Button */}
                <Button
                  variant="outline"
                  className="w-full h-12 border-dashed border-2 border-slate-300 text-slate-500 hover:border-teal-500 hover:text-teal-600"
                  onClick={addField}
                  data-testid="add-field-btn"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add New Field
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
